import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CasesIcon from '@mui/icons-material/MedicalServicesOutlined';

import Grid from '@mui/material/Grid';

import {
  DashboardHeaderMobile,
  DashboardMessage,
  RadiologyCaseContainer,
  DashboardCasesTableContainer,
} from '../../components/dashboard';
import {
  CaseHospitalsChart,
  CaseProceduresChart,
  CaseSurgeonsPieChart,
  FutureCasesChart,
} from '../../components/dashboard/charts';
import { ActivityTable } from '../../components/activity';
import RestrictComponent from '../../components/shared/RestrictComponent';
import LoadScreen from '../../components/load-screen';
import StatsBox from '../../components/shared/StatsBox';

import { toggleSidebar } from '../../actions/sidebarActions';
import { fetchCases } from '../../actions/casesActions';

import { withFirebase } from '../../firebase';

import userRoles from '../../constants/userRoles';

import './dashboard-page.scss';

const DashboardPage = ({ currentUser, activityList, users, fetchCases, trainingMode }) => {
  const theme = useTheme();
  // const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [fetching, setFetching] = useState(false);
  const [cases, setCases] = useState([]);
  const [thisMonthCases, setThisMonthCases] = useState(0);
  const [lastMonthCases, setLastMonthCases] = useState(0);
  const [lastYearCases, setLastYearCases] = useState(0);
  const [futureCases, setFutureCases] = useState([]);
  const [last30DaysCases, setLast30DaysCases] = useState([]);

  useEffect(() => {
    fetchLast12MonthsCases().catch();
    fetchMonthlyCases().catch();
  }, [trainingMode, currentUser?.uid]);

  // useEffect(() => {
  //   fetchFutureCases().catch();
  // }, [trainingMode]);

  const fetchMonthlyCases = async () => {
    const res1 = await fetchCases({
      page: 1,
      perPage: 1,
      statuses: [],
      dateFrom: moment().startOf('month').valueOf(),
      dateTo: moment().endOf('month').valueOf(),
      trainingMode,
    });
    if (res1) {
      setThisMonthCases(res1?.total || 0);
    }
    const res2 = await fetchCases({
      page: 1,
      perPage: 1,
      statuses: [],
      dateFrom: moment().subtract(1, 'month').startOf('month').valueOf(),
      dateTo: moment().subtract(1, 'month').endOf('month').valueOf(),
      trainingMode,
    });
    if (res2) {
      setLastMonthCases(res2?.total || 0);
    }
    const res3 = await fetchCases({
      page: 1,
      perPage: 250,
      statuses: [],
      dateFrom: moment().startOf('day').valueOf(),
      dateTo: moment().add(30, 'days').endOf('day').valueOf(),
      trainingMode,
    });
    if (res3) {
      setFutureCases(res3?.cases || []);
    }
  };

  const fetchFutureCases = async () => {
    const res = await fetchCases({
      page: 1,
      statuses: [],
      dateFrom: moment().startOf('day').valueOf(),
      dateTo: moment().add(30, 'days').endOf('day').valueOf(),
      trainingMode,
    });
    if (res) {
      setFutureCases(res?.cases || []);
    }
  };

  const fetchLast12MonthsCases = async () => {
    try {
      setFetching(true);

      const perPage = 250;
      const dateFrom = moment()
        .subtract(11, 'months')
        .startOf('month')
        .valueOf();

      let page = 1;
      let totalFound = 0;
      let allCases = [];

      do {
        const res = await fetchCases({
          page,
          perPage,
          statuses: [],
          dateFrom,
          trainingMode,
        });

        if (!res) break;

        const { total = 0, cases = [] } = res;

        totalFound = total;
        allCases = [...allCases, ...cases];
        page++;

      } while (allCases.length < totalFound);

      console.log('Total cases fetched:', allCases.length);

      setCases(allCases);

      const lastYearDay = moment().subtract(1, 'year');
      const lastYearCases = allCases.filter((caseItem) => moment(caseItem.date).isAfter(lastYearDay));
      setLastYearCases(lastYearCases?.length || 0);

      const monthAgo = moment().subtract(30, 'days').startOf('day');
      const last30Days = allCases.filter((caseItem) => moment(caseItem.date)?.isAfter(monthAgo) && moment(caseItem.date)?.isBefore(moment()));
      setLast30DaysCases(last30Days);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="dashboard-page">
      {fetching && <LoadScreen />}
      {/*{isDesktop && <DashboardHeader />}*/}
      {isMobile && <DashboardHeaderMobile />}

      {(!currentUser.role || !currentUser.emailVerified || currentUser.adminPassword) && (
        <DashboardMessage
          emailVerified={currentUser.emailVerified}
          adminPassword={!!currentUser.adminPassword}
          userData={currentUser}
        />
      )}

      {currentUser.role === userRoles.RADIOLOGY.name ? (
        <RadiologyCaseContainer />
      ) : (
        <RestrictComponent>
          <Grid container spacing={3} sx={{ mb: 3, display: { xs: 'none', md: 'flex' } }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <StatsBox title='This Month' number={thisMonthCases} previousNumber={lastMonthCases} icon={<CasesIcon />} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <StatsBox title='Last Month' number={lastMonthCases} icon={<CasesIcon />} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <StatsBox title='YTD (Year to Date)' number={lastYearCases} icon={<CasesIcon />} />
            </Grid>
          </Grid>

          <DashboardCasesTableContainer cases={futureCases} />

          <Grid container spacing={3} sx={{ mt: 3 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <CaseProceduresChart cases={cases} />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <FutureCasesChart cases={futureCases} />
            </Grid>
            {currentUser.role === userRoles.GLOBAL_ADMIN.name && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CaseSurgeonsPieChart cases={last30DaysCases} />
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <CaseHospitalsChart cases={last30DaysCases} />
                </Grid>
              </>
            )}
          </Grid>

          <Box sx={{ mt: 3 }}>
            <ActivityTable
              activityList={activityList}
              dashboardActivities
              users={users}
              onlyRecent
            />
          </Box>
        </RestrictComponent>
      )}
    </div>
  );
};

DashboardPage.propTypes = {
  currentUser: PropTypes.object,
  activityList: PropTypes.array,
  cases: PropTypes.array,
  users: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.user.currentUser,
    activityList: state.activity.list,
    users: state.users.list || [],
    trainingMode: state.sidebar.trainingMode,
  };
};

const mapDispatchToProps = (dispatch) => ({
  toggleSidebar: () => dispatch(toggleSidebar()),
  fetchCases: (params) => dispatch(fetchCases(params)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withFirebase(DashboardPage));
